import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { SlidersHorizontal } from "lucide-react";
import { api } from "@/lib/api";
import { Overline } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";

const SORTS = [
  { key: "", label: "Featured" },
  { key: "price_asc", label: "Price: Low → High" },
  { key: "price_desc", label: "Price: High → Low" },
  { key: "rating", label: "Top Rated" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const category = params.get("category") || "all";
  const flavour = params.get("flavour") || "all";
  const sort = params.get("sort") || "";

  useEffect(() => {
    document.title = "Shop — Dignity Food Kitchen | Premium Soya Nachos & Chips";
    api.get("/categories").then(({ data }) => setCategories(data.categories));
  }, []);

  useEffect(() => {
    api.get("/products", { params: { category, flavour, sort } }).then(({ data }) => setProducts(data.products));
  }, [category, flavour, sort]);

  const update = (key, val) => {
    const next = new URLSearchParams(params);
    if (val && val !== "all" && val !== "") next.set(key, val); else next.delete(key);
    setParams(next);
  };

  return (
    <div className="min-h-screen bg-black pt-28">
      <div className="mx-auto max-w-7xl px-5 pb-24 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
          <Overline>Our Products</Overline>
          <h1 className="mt-3 font-display text-6xl leading-none tracking-tight text-cream sm:text-8xl">
            SHOP THE <span className="text-gold-gradient">CRUNCH</span>
          </h1>
        </motion.div>

        {/* Category chips */}
        <div className="no-scrollbar mt-10 flex gap-3 overflow-x-auto pb-2">
          {categories.map((c) => (
            <button key={c.key} onClick={() => update("category", c.key)}
              data-testid={`category-${c.key.replace(/\s/g, "-")}`}
              className={`shrink-0 rounded-full border px-5 py-2 font-oswald text-xs uppercase tracking-[0.12em] transition-colors ${
                category === c.key ? "gold-gradient border-transparent text-black" : "border-white/15 text-white/70 hover:border-gold/50"
              }`}>
              {c.label}
            </button>
          ))}
        </div>

        {/* Sort */}
        <div className="mt-6 flex items-center justify-between">
          <span className="text-sm text-white/40">{products.length} products</span>
          <div className="flex items-center gap-2">
            <SlidersHorizontal size={15} className="text-gold" />
            <select value={sort} onChange={(e) => update("sort", e.target.value)}
              data-testid="sort-select"
              className="rounded-full border border-white/15 bg-transparent px-4 py-2 font-oswald text-xs uppercase tracking-wide text-cream outline-none">
              {SORTS.map((s) => <option key={s.key} value={s.key} className="bg-forest-deep">{s.label}</option>)}
            </select>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
        {products.length === 0 && (
          <p className="mt-20 text-center font-oswald text-xl text-white/40">No products found. Try another filter.</p>
        )}
      </div>
    </div>
  );
}
